import {
    Add as AddIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Error as ErrorIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { defaultLocale } from '../../constants/locale';
import { useFormatter } from '../../context/FormatterContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Platform } from '../../services/integration.types';
import { Integration } from '../../services/mocks/data/integrations';
import {
    useUserIntegrationsService,
} from '../../services/user-integrations.service';
import { useUserService } from '../../services/user.service';
import { QUERY_KEYS } from '../../utils/queryKeys';
import ResponsiveTooltip from '../ResponsiveTooltip';
import DeleteIntegrationConfirmModal from './DeleteIntegrationConfirmModal';
import IntegrationExchangeCsvFormComponent from './integration-forms/ExchangeCsv';
import IntegrationAssetsModal from './IntegrationAssetsModal';
import IntegrationSelectModal from './IntegrationSelectModal';

interface IntegrationsOverviewProps {
    integrations: Integration[] | undefined;
    onSync: (integration: Integration) => void;
    onDelete: (integration: Integration) => void;
    deletingIntegrationName: string | null;
    isLoading: boolean;
}

const StatusChip: React.FC<{
    status: Integration['status'];
    onClick?: () => void;
}> = ({ status, onClick }) => {
    const { t } = useTranslation();
    const getStatusConfig = (status: Integration['status']['status']) => {
        switch (status) {
            case 'in_progress':
                return {
                    label: t('integrations.overview.status.syncing'),
                    bgColor: '#ffca27',
                    textColor: '#000000',
                    icon: (
                        <RefreshIcon
                            fontSize="small"
                            sx={{
                                animation: 'spin 2s linear infinite',
                                '@keyframes spin': {
                                    '0%': {
                                        transform: 'rotate(0deg)',
                                    },
                                    '100%': {
                                        transform: 'rotate(360deg)',
                                    },
                                },
                                marginRight: '4px',
                                fontSize: '0.875rem',
                            }}
                        />
                    ),
                };
            case 'done':
                return {
                    label: t('integrations.overview.status.synced'),
                    bgColor: '#15B834',
                    textColor: '#FFFFFF',
                    icon: <CheckCircleIcon />,
                };
            case 'error':
                return {
                    label: t('integrations.overview.status.error'),
                    bgColor: '#D32F2F',
                    textColor: '#FFFFFF',
                    icon: <ErrorIcon />,
                };
        }
    };

    const config = getStatusConfig(status.status);

    // Determine if the chip should be clickable (only when status is error)
    const isClickable = status.status === 'error' && onClick;

    return (
        <ResponsiveTooltip title={status.message} arrow placement="top">
            <Typography
                variant="body2"
                onClick={(e) => {
                    if (isClickable) {
                        e.stopPropagation();
                        onClick?.();
                    }
                }}
                sx={{
                    color: config.textColor,
                    fontWeight: 'bold',
                    bgcolor: config.bgColor,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable
                        ? {
                              opacity: 0.9,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }
                        : {},
                    '& svg': {
                        mr: 0.5,
                        fontSize: '1rem',
                    },
                }}
            >
                {config.icon}
                {config.label}
            </Typography>
        </ResponsiveTooltip>
    );
};

const IntegrationsOverview: React.FC<IntegrationsOverviewProps> = ({
    integrations,
    onSync,
    onDelete,
    deletingIntegrationName,
    isLoading,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { showNotification } = useNotification();
    const { formatCurrency } = useFormatter();
    const userIntegrationsService = useUserIntegrationsService();
    const queryClient = useQueryClient();

    // State for edit mode
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuIntegration, setMenuIntegration] = useState<Integration | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editIntegration, setEditIntegration] = useState<Integration | null>(null);
    const [editImportType, setEditImportType] = useState<string>('api');
    const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
    const [csvUploadPlatform, setCsvUploadPlatform] = useState<Platform | null>(null);

    // Use React Query for data fetching
    const queryFn = async () => {
        try {
            const platforms = await userIntegrationsService.getUserIntegrations();
            console.log('Fetched user integrations with React Query:', platforms);
            return platforms;
        } catch (error) {
            console.error('Error fetching user integrations:', error);
            return [];
        }
    };

    const {
        data: userPlatforms = [],
        isLoading: isLoadingUserIntegrations,
        refetch: refetchUserIntegrations,
    } = useQuery({
        queryKey: ['userIntegrations'],
        queryFn,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 60000, // Cache for 1 minute
    });

    const userService = useUserService();
    
    // Check notifications to determine if banner should be shown
    const { data: notifications, isLoading: isLoadingNotifications } = useQuery({
        queryKey: [QUERY_KEYS.USER_NOTIFICATIONS],
        queryFn: () => userService.getNotifications(20),
        refetchOnWindowFocus: false,
    });

    // Check for selected integrations that are already integrated and update their status
    useEffect(() => {
        if (integrations && userPlatforms) {
            // Find platforms with 'selected' status that are already integrated
            const selectedPlatforms = userPlatforms.filter((platform) => platform.status === 'selected');

            if (selectedPlatforms.length > 0) {
                // Check each selected platform against the integrations list
                selectedPlatforms.forEach((platform) => {
                    // Check if this platform is already integrated
                    const isAlreadyIntegrated = integrations.some(
                        (integration) =>
                            integration.platformType == platform.platform_type &&
                            integration.platformName == platform.name
                    );

                    if (isAlreadyIntegrated) {
                        // Update the status to 'integrated'
                        console.log(`Platform ${platform.name} is already integrated, updating status...`);
                        userIntegrationsService
                            .updateIntegrationStatus(platform.name, platform.platform_type)
                            .then(() => {
                                console.log(`Updated integration status for ${platform.name} to 'integrated'`);
                                // Invalidate the integration setup state query to re-enable navigation
                                queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
                            })
                            .catch((error) => {
                                console.error(`Error updating integration status for ${platform.name}:`, error);
                            });
                    }
                });
            }
        }
    }, [integrations, userPlatforms]);

    // Filter platforms that have not been removed
    const userPlatformsFiltered = userPlatforms.filter(
        (platform) =>
            platform.status !== 'removed' &&
            platform.status !== undefined &&
            !(integrations || []).some(
                (integration) =>
                    integration.platformType == platform.platform_type && integration.platformName == platform.name
            )
    );

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, integration: Integration) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuIntegration(integration);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuExited = () => {
        setMenuIntegration(null);
    };

    const handleSync = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            onSync(menuIntegration);
        }
        handleMenuClose();
    };

    const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setIntegrationToDelete(menuIntegration);
            setDeleteConfirmOpen(true);
        }
        handleMenuClose();
    };

    const handleViewAssets = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setSelectedIntegration(menuIntegration);
        }
        handleMenuClose();
    };

    const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        handleMenuClose();

        if (menuIntegration) {
            setEditIntegration(menuIntegration);
            setEditImportType('api');
            setIsAddModalOpen(true);
        }
    };

    const handleImportCSV = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setEditIntegration(menuIntegration);
            setIsAddModalOpen(true);
            setEditImportType('csv');
        }
        handleMenuClose();
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditIntegration(null);
        setEditImportType('api');
    };

    const handleConfirmDelete = () => {
        if (integrationToDelete) {
            onDelete(integrationToDelete);
            setDeleteConfirmOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setIntegrationToDelete(null);
    };

    const SkeletonRow = () => (
        <TableRow>
            <TableCell>
                <Skeleton variant="text" width={120} />
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width={100} />
            </TableCell>
        </TableRow>
    );

    const welcomeBoxStyles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3',
    };

    return (
        <>
            {/* Welcome Box */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    justifyContent: 'space-between',
                    mb: 3,
                    gap: 2,
                }}
            >
                {/* Left side - Welcome box or empty space */}
                <Box
                    sx={{
                        flexGrow: 1,
                        mr: { xs: 0, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { sm: 'calc(100% - 180px)' },
                    }}
                >
                    {/* Show the welcome box only if there are no notifications (same as menu disabled state) */}
                    {!isLoadingNotifications && (!notifications || notifications.length === 0) && (
                            <Alert
                                severity="info"
                                icon={false}
                                sx={{
                                    backgroundColor: welcomeBoxStyles.backgroundColor,
                                    color: welcomeBoxStyles.textPrimary,
                                    fontWeight: 'bold',
                                    fontSize: '1.1rem',
                                    border: `1px solid ${welcomeBoxStyles.border}`,
                                    borderRadius: 1.5,
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        color: welcomeBoxStyles.textPrimary,
                                    }}
                                >
                                    🏁 {t('integrations.overview.welcomeMessage')}
                                </Typography>
                            </Alert>
                        )}
                </Box>

                {/* Right side - Add Integration button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        mt: { xs: 2, sm: 0 },
                        alignSelf: { xs: 'center', sm: 'flex-end' },
                        minWidth: '170px',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        height: { xs: 'auto', sm: userPlatformsFiltered.length > 0 ? '100%' : 'auto' },
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        sx={{
                            whiteSpace: 'nowrap',
                            minWidth: '170px',
                        }}
                    >
                        {t('integrations.overview.addButton')}
                    </Button>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    px: { xs: 1, sm: 2, md: 4 },
                    py: 1.5,
                    overflowX: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.divider, 0.6),
                        borderRadius: 3,
                    },
                    '& .MuiTableBody-root tr:last-child td': {
                        borderBottom: '0 !important',
                    },
                    '& .MuiTableBody-root tr:last-child th': {
                        borderBottom: '0 !important',
                    },
                }}
            >
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            py: 2.5,
                            borderBottom: 'none',
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            px: { xs: 1, sm: 2 },
                        },
                        '& thead .MuiTableCell-root': {
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                            py: 2,
                        },
                        '& tbody .MuiTableRow-root:not(:last-child) .MuiTableCell-root': {
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                        },
                        '& .MuiTypography-root': {
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        },
                        tableLayout: { xs: 'auto', md: 'fixed' },
                        borderCollapse: 'collapse',
                        width: '100%',
                        minWidth: {
                            xs: '650px',
                            sm: '850px',
                            md: '100%',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.account')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.transactions')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.assets')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.value')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.lastSync')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.status')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.actions')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <>
                                <SkeletonRow key="skeleton-1" />
                                <SkeletonRow key="skeleton-2" />
                                <SkeletonRow key="skeleton-3" />
                            </>
                        ) : integrations === undefined || integrations === null || integrations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="body1" sx={{ p: 3, color: theme.palette.text.secondary }}>
                                        {t('integrations.overview.noIntegrations')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {integrations.map((integration) => (
                                    <TableRow
                                        key={integration.name + integration.jobId}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }}
                                        onClick={() => {
                                            if (!anchorEl) {
                                                setSelectedIntegration(integration);
                                            }
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        integration.logoUri
                                                            ? 'https://cointracking.info/' + integration.logoUri
                                                            : ''
                                                    }
                                                    alt={integration.name}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                    }}
                                                />
                                                <Box>
                                                    {integration.walletAddress ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                {integration.name}
                                                            </Typography>
                                                            <ResponsiveTooltip
                                                                title={integration.walletAddress}
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (integration.walletAddress) {
                                                                            navigator.clipboard.writeText(
                                                                                integration.walletAddress
                                                                            );
                                                                            showNotification(
                                                                                t('common.copied'),
                                                                                'success'
                                                                            );
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        ml: 0.5,
                                                                        p: 0.5,
                                                                        color: theme.palette.text.secondary,
                                                                        '&:hover': {
                                                                            color: theme.palette.primary.main,
                                                                        },
                                                                    }}
                                                                >
                                                                    <ContentCopyIcon
                                                                        fontSize="small"
                                                                        sx={{ fontSize: '0.85rem' }}
                                                                    />
                                                                </IconButton>
                                                            </ResponsiveTooltip>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {integration.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {integration.transactionsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {integration.assetsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {formatCurrency(integration.value)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {formatDistanceToNow(new Date(integration.endTime * 1000), {
                                                    addSuffix: true,
                                                    locale: defaultLocale,
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <StatusChip
                                                status={integration.status}
                                                onClick={
                                                    integration.status.status === 'error'
                                                        ? () => {
                                                              // Set the current integration as the one to edit
                                                              setMenuIntegration(integration);
                                                              setEditIntegration(integration);
                                                              setEditImportType('api');
                                                              setIsAddModalOpen(true);
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(event) => handleMenuClick(event, integration)}
                                                size="small"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                    },
                                                }}
                                            >
                                                {deletingIntegrationName === integration.name ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <MoreVertIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={handleMenuClose}
                                                TransitionProps={{
                                                    onExited: handleMenuExited,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                                slotProps={{
                                                    paper: {
                                                        sx: {
                                                            width: 200,
                                                            maxWidth: '100%',
                                                            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                                                            mt: 0.5,
                                                            '& .MuiList-root': {
                                                                py: 0.5,
                                                            },
                                                        },
                                                    },
                                                }}
                                                MenuListProps={{
                                                    dense: true,
                                                    onClick: (e) => e.stopPropagation(),
                                                }}
                                                sx={{
                                                    '& .MuiMenuItem-root': {
                                                        py: 1,
                                                        px: 2,
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                        },
                                                    },
                                                    '& .MuiListItemIcon-root': {
                                                        minWidth: 32,
                                                        color: theme.palette.text.secondary,
                                                    },
                                                    '& .MuiListItemText-primary': {
                                                        fontWeight: 600,
                                                        fontSize: '0.875rem',
                                                    },
                                                }}
                                            >
                                                {menuIntegration?.jobId &&
                                                    menuIntegration?.status?.status !== 'error' && (
                                                        <MenuItem
                                                            onClick={handleSync}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <RefreshIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.sync')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                {menuIntegration?.platformType === 'exchange' &&
                                                    menuIntegration?.jobId && (
                                                        <MenuItem
                                                            onClick={handleEdit}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <EditIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.edit')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                {menuIntegration?.importTypes &&
                                                    menuIntegration.importTypes.includes('csv') && (
                                                        <MenuItem
                                                            onClick={handleImportCSV}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <UploadFileIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.importCsv')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                <MenuItem
                                                    onClick={handleViewAssets}
                                                    disabled={!!deletingIntegrationName}
                                                >
                                                    <ListItemIcon>
                                                        <AssessmentIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={t('integrations.overview.actions.viewAssets')}
                                                    />
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={handleDelete}
                                                    disabled={
                                                        !!deletingIntegrationName ||
                                                        menuIntegration?.status?.status === 'in_progress'
                                                    }
                                                    sx={{
                                                        color: theme.palette.error.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.error.main,
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <DeleteIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={t('integrations.overview.actions.delete')} />
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <IntegrationSelectModal
                open={isAddModalOpen}
                onClose={handleCloseAddModal}
                selectedPlatformName={editIntegration?.platformName}
                selectedImportType={editImportType}
            />

            {selectedIntegration && (
                <IntegrationAssetsModal
                    open={!!selectedIntegration}
                    onClose={() => setSelectedIntegration(null)}
                    integration={selectedIntegration}
                />
            )}

            <DeleteIntegrationConfirmModal
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                integration={integrationToDelete}
                isDeleting={!!deletingIntegrationName && integrationToDelete?.name === deletingIntegrationName}
            />

            {/* Direct CSV Upload Modal for Custom Integrations */}
            {showCsvUploadModal && csvUploadPlatform && (
                <Dialog
                    open={showCsvUploadModal}
                    onClose={() => setShowCsvUploadModal(false)}
                    maxWidth="lg"
                    fullWidth
                    BackdropProps={{
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                        },
                    }}
                    PaperProps={{
                        sx: {
                            width: '80%',
                            maxWidth: '1000px',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            bgcolor: (theme) => theme.palette.background.paper,
                            '& .MuiDialogContent-root': {
                                bgcolor: (theme) => theme.palette.background.paper,
                            },
                        },
                    }}
                >
                    <DialogTitle sx={{ bgcolor: (theme) => theme.palette.background.paper }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                {csvUploadPlatform.display_name}
                            </Typography>
                            <IconButton
                                aria-label="close"
                                onClick={() => setShowCsvUploadModal(false)}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <IntegrationExchangeCsvFormComponent
                                platform={csvUploadPlatform}
                                onJobCreated={() => {
                                    setShowCsvUploadModal(false);
                                    if (onSync && editIntegration) onSync(editIntegration);
                                }}
                                isOnboarding={false}
                                importer={{
                                    id: 0,
                                    customText: '',
                                    displayName: 'File Upload',
                                    position: 0,
                                    type: 'excel',
                                    uri: '/import/import_xls',
                                    uploadFile: '',
                                    importFolder: '',
                                    customFields: [],
                                }}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default IntegrationsOverview;
